/**
 * Traducciones de los motivos que llegan en "vehicle.satrackGpsLink.description".
 * El backend propaga sin traducir el mensaje del microservicio de automatización de Satrack, y
 * cuando ese mensaje viene vacío propaga el código del error, así que ambas formas son llaves válidas.
 * Las llaves están normalizadas: en minúsculas, sin espacios repetidos y sin el punto final.
 */
export const SatrackGpsLinkErrors: { [key: string]: string } = {
  // Validaciones de la matrícula contra el catálogo de Satrack
  'the plate does not have a satrack gps device': 'El vehículo no tiene un dispositivo GPS Satrack asociado.',
  'satrack_vehicle_not_satrack_gps': 'El vehículo no tiene un dispositivo GPS Satrack asociado.',
  'the plate is already registered in satrack, so it cannot be linked again':
    'La matrícula ya está registrada en Satrack, por lo que no se puede volver a vincular.',
  'satrack_vehicle_already_linked':
    'La matrícula ya está registrada en Satrack, por lo que no se puede volver a vincular.',

  // Catálogo de proveedores de GPS
  'providername was not found in the satrack gps providers catalog':
    'El proveedor de GPS del vehículo no existe en el catálogo de Satrack.',
  'satrack_gps_provider_not_found': 'El proveedor de GPS del vehículo no existe en el catálogo de Satrack.',
  'satrack gps providers catalog returned an invalid response':
    'El catálogo de proveedores de GPS de Satrack devolvió una respuesta inválida.',
  'satrack_gps_providers_invalid_response':
    'El catálogo de proveedores de GPS de Satrack devolvió una respuesta inválida.',
  'satrack_gps_providers_failed': 'No fue posible consultar el catálogo de proveedores de GPS de Satrack.',

  // Catálogo de tipos de vehículo
  'vehicletypeid and vehicletypename do not match a satrack vehicle type':
    'El tipo de vehículo no corresponde a un tipo válido en Satrack.',
  'satrack_vehicle_type_invalid': 'El tipo de vehículo no corresponde a un tipo válido en Satrack.',
  'vehicletypeid and vehicletypename must be sent together':
    'El tipo de vehículo está incompleto: falta su identificador o su nombre.',
  'satrack vehicle types catalog returned an invalid response':
    'El catálogo de tipos de vehículo de Satrack devolvió una respuesta inválida.',
  'satrack_vehicle_types_invalid_response':
    'El catálogo de tipos de vehículo de Satrack devolvió una respuesta inválida.',
  'satrack_vehicle_types_failed': 'No fue posible consultar el catálogo de tipos de vehículo de Satrack.',

  // Consulta del vehículo en Satrack
  'satrack vehicle registration status returned an invalid response':
    'Satrack devolvió una respuesta inválida al consultar el estado de registro de la matrícula.',
  'satrack_vehicle_registration_status_invalid_response':
    'Satrack devolvió una respuesta inválida al consultar el estado de registro de la matrícula.',
  'satrack_vehicle_registration_status_failed':
    'No fue posible consultar en Satrack el estado de registro de la matrícula.',
  'satrack_vehicles_failed': 'No fue posible consultar los vehículos en Satrack.',
  'satrack_vehicle_gps_link_failed': 'Satrack rechazó la solicitud de vinculación del GPS.',

  // Sesión y credenciales de Satrack
  'cannot link vehicle because clientid was not found in the satrack session':
    'No fue posible vincular el vehículo porque la sesión de Satrack no tiene cliente asociado.',
  'satrack_client_id_required':
    'No fue posible vincular el vehículo porque la sesión de Satrack no tiene cliente asociado.',
  'satrack login succeeded but tokenlocation was not present':
    'El inicio de sesión en Satrack no devolvió el token necesario para continuar.',
  'satrack_token_not_found': 'El inicio de sesión en Satrack no devolvió el token necesario para continuar.',
  'satrack_login_failed': 'No fue posible iniciar sesión en Satrack.',
  'invalid_satrack_login': 'Las credenciales configuradas para Satrack están incompletas.',
  'invalid internal token': 'El token interno de la integración con Satrack es inválido.',
  'invalid_internal_token': 'El token interno de la integración con Satrack es inválido.',
  'global_process_manual_login_forbidden': 'El proceso global no admite credenciales manuales.',

  // Comunicación con Satrack
  'satrack request timed out': 'La solicitud a Satrack superó el tiempo de espera.',
  'satrack_request_timeout': 'La solicitud a Satrack superó el tiempo de espera.',
  'satrack_request_failed': 'No fue posible comunicarse con Satrack.',
  'url must be an absolute url': 'La URL configurada para Satrack es inválida.',
  'only https satrack urls are allowed': 'Solo se permiten URLs HTTPS de Satrack.',
  'invalid_satrack_url': 'La URL configurada para Satrack es inválida.',
  'satrack_host_not_allowed': 'La URL configurada para Satrack no está permitida.',

  // Datos de la solicitud
  'companyid header is required': 'La solicitud a Satrack no incluyó la empresa.',
  'company_id_required': 'La solicitud a Satrack no incluyó la empresa.',
  'plate is required': 'La solicitud a Satrack no incluyó la matrícula.',
  'invalid_vehicle_plate': 'La solicitud a Satrack no incluyó la matrícula.',
  'body must be an object': 'La solicitud enviada a Satrack tiene un formato inválido.',
  'invalid_request_body': 'La solicitud enviada a Satrack tiene un formato inválido.',
  'invalid_link_vehicle_gps': 'Los datos enviados a Satrack para vincular el GPS son inválidos.',
  'loadcapacity must be a non-empty string or a number': 'La capacidad de carga del vehículo es inválida.',
  'modifieddate must be a string or null': 'La fecha de modificación enviada a Satrack es inválida.'
};

/**
 * Motivos que Satrack arma en tiempo de ejecución (traen el código HTTP o el nombre del campo),
 * por lo que no se pueden resolver con una llave exacta. Se evalúan en orden y "$1" recibe lo capturado.
 */
export const SatrackGpsLinkErrorPatterns: Array<{ pattern: RegExp, message: string }> = [
  {
    pattern: /^satrack vehicle gps link request failed \(http (\d+)\)$/i,
    message: 'Satrack rechazó la solicitud de vinculación del GPS (HTTP $1).'
  },
  {
    pattern: /^satrack vehicle registration status request failed \(http (\d+)\)$/i,
    message: 'No fue posible consultar en Satrack el estado de registro de la matrícula (HTTP $1).'
  },
  {
    pattern: /^satrack gps providers request failed \(http (\d+)\)$/i,
    message: 'No fue posible consultar el catálogo de proveedores de GPS de Satrack (HTTP $1).'
  },
  {
    pattern: /^satrack vehicle types request failed \(http (\d+)\)$/i,
    message: 'No fue posible consultar el catálogo de tipos de vehículo de Satrack (HTTP $1).'
  },
  {
    pattern: /^satrack vehicles request failed \(http (\d+)\)$/i,
    message: 'No fue posible consultar los vehículos en Satrack (HTTP $1).'
  },
  {
    pattern: /^satrack login failed \(http (\d+)\)$/i,
    message: 'No fue posible iniciar sesión en Satrack (HTTP $1).'
  },
  {
    pattern: /^satrack request failed: /i,
    message: 'No fue posible comunicarse con Satrack.'
  },
  {
    pattern: /^host .+ is not allowed$/i,
    message: 'La URL configurada para Satrack no está permitida.'
  },
  {
    pattern: /^(\w+) is required$/i,
    message: 'La solicitud a Satrack no incluyó el dato "$1".'
  },
  {
    pattern: /^(\w+) must be a (?:number|string|boolean)$/i,
    message: 'El dato "$1" enviado a Satrack es inválido.'
  }
];
